import {
  FloatingFormItem,
  InputDecimal,
  InputDate,
  CompaniesInfiniteSelectField,
} from '@/components/_common';
import EmployeesInfiniteSelectField from '@/components/_common/EmployeesInfiniteSelectField';
import InsurerInputSearch from '@/components/_common/InsurerInputSearch';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import {
  WorkOrderComplementType,
  WorkOrderComplementTankLevel,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';
import { WorkOrderImportGeneralFormValues } from '@/core/domain/workorder/WorkOrdersImports/WorkOrderImports.types';
import { useDefaultEmptyFieldError } from '@/hooks';
import { isAppPortocar } from '@/index';
import Authorities from '@/types/Authority';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import { Col, Form, FormInstance, Input, Row, Select, Typography } from 'antd';
import { useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

const { Title } = Typography;

type Props = {
  form: FormInstance<WorkOrderImportGeneralFormValues>;
};

const WorkorderImportForm: React.FC<Props> = ({ form }) => {
  const defaultRule = useDefaultEmptyFieldError();
  const intl = useIntl();
  const companyId = Form.useWatch('companyId', form);

  const { data: configBudget, mutateAsync: configBudgetMutate } =
    useWorkorderConfigurationCompany();

  useEffect(() => {
    if (companyId) {
      configBudgetMutate(companyId);
    }
  }, [companyId]);

  // TODO: colocar tipagem
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function onValuesChange(field: any) {
    if (field.companyId) {
      form.resetFields(['estimatorId']);
    }
  }

  return (
    <>
      <Form form={form} onValuesChange={onValuesChange}>
        <Form.Item hidden name="SSLocaliza" />
        <Form.Item hidden name="items" />
        <Form.Item hidden name="origin" />
        <Form.Item hidden name="ciliaBudgetNumber" />

        <Row gutter={[16, 8]}>
          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="companyId"
              rules={[defaultRule]}
              label={intl.formatMessage({
                id: 'workOrders.domain.company',
              })}
            >
              <CompaniesInfiniteSelectField />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="estimatorId"
              rules={[defaultRule]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.estimator',
              })}
            >
              <EmployeesInfiniteSelectField queryFilter={{ estimator: true }} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="type"
              rules={[defaultRule]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.type',
              })}
              initialValue={WorkOrderComplementType.WorkOrder}
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                options={Object.values(WorkOrderComplementType).map((type) => ({
                  label: intl.formatMessage({
                    id: `workOrders.${
                      isAppPortocar && type === WorkOrderComplementType.Courtesy
                        ? 'domain.enums.work_order_type.insideCourtesy'
                        : type
                    }`,
                  }),
                  value: type,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={6}>
            <FloatingFormItem
              name="customerType"
              rules={[defaultRule]}
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.customerType',
              })}
              getValueFromEvent={(type) => {
                form.resetFields([
                  'insurerId',
                  'accidentNumber',
                  'insurerDeductibleAmount',
                ]);
                return type;
              }}
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                options={Object.values(CustomerType).map((a) => ({
                  label: intl.formatMessage({ id: `workOrders.${a}` }),
                  value: a,
                }))}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <Form.Item
              shouldUpdate={(prev, curr) =>
                prev.customerType !== curr.customerType
              }
              noStyle
            >
              {({ getFieldValue }) => {
                const customerType = getFieldValue('customerType');

                return (
                  customerType === CustomerType.Insurance && (
                    <Row gutter={[16, 8]}>
                      <Col span={24}>
                        <Title level={5}>
                          <FormattedMessage id="workOrders.domain.complements.insurer" />
                        </Title>
                      </Col>
                      <Col span={24} md={8}>
                        <Form.Item hidden name="insurerId">
                          <Input />
                        </Form.Item>
                        <FloatingFormItem
                          name="insurerName"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.insurer',
                          })}
                          rules={[defaultRule]}
                        >
                          <InsurerInputSearch
                            fieldNameId="insurerId"
                            onInsurerSelect={(insurer) =>
                              form.setFieldsValue({
                                insurerId: insurer.id,
                                insurerName: insurer.name,
                              })
                            }
                          />
                        </FloatingFormItem>
                      </Col>
                      <Col span={24} md={8}>
                        <FloatingFormItem
                          name="accidentNumber"
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.accidentNumber',
                          })}
                        >
                          <Input />
                        </FloatingFormItem>
                      </Col>
                      <Col span={24} md={8}>
                        <FloatingFormItem
                          name="insurerDeductibleAmount"
                          initialValue={0}
                          label={intl.formatMessage({
                            id: 'workOrders.domain.complements.insurerDeductibleAmount',
                          })}
                        >
                          <InputDecimal />
                        </FloatingFormItem>
                      </Col>
                    </Row>
                  )
                );
              }}
            </Form.Item>
          </Col>

          <Col span={24} style={{ width: '100%' }}>
            <Row gutter={[16, 8]}>
              <Col xs={24} sm={24} md={8} lg={4}>
                <FloatingFormItem
                  name="entryDate"
                  rules={[defaultRule]}
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.entryDate',
                  })}
                >
                  <InputDate showTime />
                </FloatingFormItem>
              </Col>

              <Col xs={24} sm={24} md={8} lg={5}>
                <FloatingFormItem
                  name="authorizationDate"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.authorizationDate',
                  })}
                >
                  <InputDate disabled showTime />
                </FloatingFormItem>
              </Col>

              <Col xs={24} sm={24} md={8} lg={5}>
                <FloatingFormItem
                  name="closingDate"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.closingDate',
                  })}
                >
                  <InputDate disabled showTime />
                </FloatingFormItem>
              </Col>

              <Col xs={24} sm={24} md={12} lg={5}>
                <FloatingFormItem
                  name="estimatedLeaveDate"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.estimatedLeaveDate',
                  })}
                  rules={[
                    {
                      required:
                        !hasPermissions([
                          Authorities.MOVIMENTATION_WORKORDER_ALLOW_EMPTY_ESTIMATED_LEAVE_DATE,
                        ]) || configBudget?.alwaysGenerateActivityByWorkOrder,
                    },
                  ]}
                >
                  <InputDate showTime />
                </FloatingFormItem>
              </Col>

              <Col xs={24} sm={24} md={12} lg={5}>
                <FloatingFormItem
                  name="returnDate"
                  label={intl.formatMessage({
                    id: 'workOrders.domain.complements.returnDate',
                  })}
                >
                  <InputDate showTime />
                </FloatingFormItem>
              </Col>
            </Row>
          </Col>
          <Col xs={24} sm={24} md={8}>
            <FloatingFormItem
              name="prisma"
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.prisma',
              })}
              rules={[
                {
                  required: configBudget?.prisma,
                  message: intl.formatMessage({
                    id: 'workOrders.fieldRequiredByConfigCompany',
                  }),
                },
              ]}
            >
              <Input />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <FloatingFormItem
              name="currentKilometers"
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.currentKilometers',
              })}
              rules={[
                {
                  required: configBudget?.isKmRequired,
                  message: intl.formatMessage({
                    id: 'workOrders.fieldRequiredByConfigCompany',
                  }),
                },
              ]}
            >
              <InputDecimal min={0} fractions={0} max={5000000} />
            </FloatingFormItem>
          </Col>

          <Col xs={24} sm={24} md={8}>
            <FloatingFormItem
              name="tankLevel"
              label={intl.formatMessage({
                id: 'workOrders.domain.complements.tankLevel',
              })}
              rules={[
                {
                  required: configBudget?.tankLevel,
                  message: intl.formatMessage({
                    id: 'workOrders.fieldRequiredByConfigCompany',
                  }),
                },
              ]}
            >
              <Select
                allowClear
                style={{ width: '100%' }}
                options={Object.values(WorkOrderComplementTankLevel).map(
                  (a) => ({
                    label: intl.formatMessage({ id: `workOrders.${a}` }),
                    value: a,
                  }),
                )}
              />
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </>
  );
};

export default WorkorderImportForm;
